(() => {
  /* global Swiper */
  const GALLERY_NAME = "product-gallery";
  const DESKTOP_MQ = "(min-width: 1024px)";

  class ProductGallery extends HTMLElement {
    #swiper = null;
    #product = null;
    #slides = [];
    #thumbnails = [];
    #thumbnailStrip = null;
    #variantId = null;
    #hiddenHolder = null;
    #boundVariantChange = null;
    #boundThumbnailClick = null;

    connectedCallback() {
      this.#slides = [...this.querySelectorAll(".product-gallery__slide")];
      this.#thumbnails = [...this.querySelectorAll("[data-gallery-thumbnail]")];
      this.#thumbnailStrip = this.querySelector("[data-gallery-thumbnails]");
      this.#variantId = this.dataset.variantId
        ? Number(this.dataset.variantId)
        : null;
      this.#product = this.#readProductJson();
      this.#enrichMediaVariantIds();
      this.#hiddenHolder = document.createElement("div");
      this.#hiddenHolder.hidden = true;
      this.append(this.#hiddenHolder);

      this.#boundVariantChange = (event) => this.#onVariantChange(event);
      this.#boundThumbnailClick = (event) => this.#onThumbnailClick(event);

      document.addEventListener("variant:change", this.#boundVariantChange);

      for (const thumbnail of this.#thumbnails) {
        thumbnail.addEventListener("click", this.#boundThumbnailClick);
      }

      this.#filterMedia(this.#variantId);
      this.#initSwiper(0, this.#featuredMediaIndex(this.#variantId));
      this.#armDeferredVideos();
    }

    disconnectedCallback() {
      document.removeEventListener("variant:change", this.#boundVariantChange);

      for (const thumbnail of this.#thumbnails) {
        thumbnail.removeEventListener("click", this.#boundThumbnailClick);
      }

      this.#destroySwiper();
    }

    #readProductJson() {
      const page = this.closest("product-page");
      const jsonNode = page?.querySelector("[data-product-json]");

      if (!jsonNode) return null;

      try {
        return JSON.parse(jsonNode.textContent);
      } catch {
        return null;
      }
    }

    /**
     * `product | json` media entries omit `variant_ids`. Rebuild them from:
     * 1) Liquid `data-variant-ids` (media.variants) on slides/thumbnails
     * 2) each variant's `featured_media`, treated as the START of that
     *    variant's run rather than its only image
     *
     * Media is merchandised in blocks: a variant's featured media opens its
     * run, and the run continues until the next variant's featured media.
     * Stamping only the marker left every other image in the block unowned,
     * so it counted as shared and leaked into every other size.
     *
     * Media before the first marker stays unowned on purpose — a hero shot
     * ahead of the blocks is global.
     */
    #enrichMediaVariantIds() {
      if (!this.#product?.media?.length) {
        return;
      }

      const mediaById = new Map(
        this.#product.media.map((item) => [Number(item.id), item]),
      );

      for (const item of this.#product.media) {
        if (!Array.isArray(item.variant_ids)) {
          item.variant_ids = [];
        }
      }

      const stampFromDom = (el) => {
        const mediaId = Number(el.dataset.mediaId);
        const mediaItem = mediaById.get(mediaId);
        if (!mediaItem || !el.dataset.variantIds) return;

        for (const id of el.dataset.variantIds.split(",")) {
          const variantId = Number(id);
          if (!variantId) continue;
          if (!mediaItem.variant_ids.includes(variantId)) {
            mediaItem.variant_ids.push(variantId);
          }
        }
      };

      for (const slide of this.#slides) stampFromDom(slide);
      for (const thumbnail of this.#thumbnails) stampFromDom(thumbnail);

      if (!this.#product?.variants?.length) return;

      const order = this.#product.media.map((item) => Number(item.id));
      const runStarts = new Map();

      for (const variant of this.#product.variants) {
        const mediaId = Number(variant.featured_media?.id);

        if (!mediaId) continue;

        const index = order.indexOf(mediaId);

        if (index < 0) continue;

        if (!runStarts.has(index)) runStarts.set(index, []);
        runStarts.get(index).push(variant.id);
      }

      const starts = [...runStarts.keys()].sort((a, b) => a - b);

      for (const [position, start] of starts.entries()) {
        const end = starts[position + 1] ?? this.#product.media.length;
        const variantIds = runStarts.get(start);

        for (let index = start; index < end; index += 1) {
          const mediaItem = this.#product.media[index];

          for (const variantId of variantIds) {
            if (!mediaItem.variant_ids.includes(variantId)) {
              mediaItem.variant_ids.push(variantId);
            }
          }
        }
      }
    }

    /**
     * A variant with no featured media has no run of its own, so there is
     * nothing to narrow to — show everything rather than only the shared
     * media, which would leave a near-empty gallery.
     */
    #variantOwnsRun(variantId) {
      const variant = this.#product?.variants?.find(
        (item) => Number(item.id) === Number(variantId),
      );

      return Boolean(variant?.featured_media?.id);
    }

    #sizeOptionIndex() {
      const options = this.#product?.options;
      if (!Array.isArray(options)) return -1;

      return options.findIndex((name) => {
        const lower = String(name || "").toLowerCase();
        return lower === "size";
      });
    }

    #variantIdsSharingSize(variantId) {
      const variants = this.#product?.variants;
      if (!Array.isArray(variants) || !variantId) return [variantId];

      const current = variants.find(
        (item) => Number(item.id) === Number(variantId),
      );
      if (!current) return [variantId];

      const sizeIndex = this.#sizeOptionIndex();
      if (sizeIndex < 0) return [Number(variantId)];

      const sizeValue =
        current[`option${sizeIndex + 1}`] || current.options?.[sizeIndex];
      if (sizeValue == null || sizeValue === "") return [Number(variantId)];

      return variants
        .filter((item) => {
          const value =
            item[`option${sizeIndex + 1}`] || item.options?.[sizeIndex];
          return value === sizeValue;
        })
        .map((item) => Number(item.id));
    }

    #onVariantChange(event) {
      const variant = event.detail?.variant;
      const eventProductId = event.detail?.product?.id;

      if (!variant?.id) return;

      if (
        this.#product?.id != null &&
        eventProductId != null &&
        Number(eventProductId) !== Number(this.#product.id)
      ) {
        return;
      }

      this.#variantId = Number(variant.id);
      this.dataset.variantId = String(this.#variantId);

      const previousCount = this.#visibleSlides().length;
      this.#filterMedia(this.#variantId);
      const nextCount = this.#visibleSlides().length;

      this.#applyMediaChange(
        previousCount,
        nextCount,
        this.#featuredMediaIndex(variant),
      );
    }

    /**
     * Swiper caches slide geometry and pagination at init, so the filtered set
     * has to be pushed into it. update() does that in place and leaves the
     * wrapper transform alone; destroy(true, true) strips every inline style
     * Swiper wrote, which collapses the track to an unstyled row for a frame
     * before the replacement instance jumps into position.
     *
     * Only slidesPerView needs the rebuild. It resolves to
     * Math.min(2, visibleCount) at the desktop breakpoint, and the arrows,
     * pagination and thumbnail strip key off the same "fewer than two"
     * boundary, so crossing it is the one case update() cannot cover.
     */
    #applyMediaChange(previousCount, nextCount, initialSlide) {
      const crossesTwoUp =
        Math.min(2, previousCount) !== Math.min(2, nextCount);

      if (!this.#swiper || crossesTwoUp) {
        this.#reinitSwiper(initialSlide);
        return;
      }

      const target = Math.min(Math.max(0, initialSlide), nextCount - 1);

      this.#swiper.update();
      this.#swiper.slideTo(target, 0);

      // slideTo fires slideChange only when the index actually moves, and the
      // visible thumbnails have just changed either way.
      this.#syncThumbnails();
    }

    /**
     * Index of the variant's featured_media among currently visible slides.
     * Shared lifestyle images often sort first in product.media; without this,
     * size switches would keep showing slide 0 instead of the size packshot.
     */
    #featuredMediaIndex(variantOrId) {
      let mediaId = null;
      let variantId = null;

      if (variantOrId && typeof variantOrId === "object") {
        mediaId = Number(variantOrId.featured_media?.id);
        variantId = Number(variantOrId.id);
      } else if (variantOrId) {
        variantId = Number(variantOrId);
      }

      if (!mediaId && variantId && this.#product?.variants) {
        const variant = this.#product.variants.find(
          (item) => Number(item.id) === variantId,
        );
        mediaId = Number(variant?.featured_media?.id);
      }

      if (!mediaId) return 0;

      const index = this.#visibleSlides().findIndex(
        (slide) => Number(slide.dataset.mediaId) === mediaId,
      );

      return index >= 0 ? index : 0;
    }

    #mediaMatchesVariant(mediaId, variantId) {
      if (!this.#product?.media || !variantId) return true;

      const mediaItem = this.#product.media.find(
        (item) => Number(item.id) === Number(mediaId),
      );

      if (!mediaItem) return true;

      const variantIds = mediaItem.variant_ids || [];

      // Empty variant_ids = shared media (always visible). Media linked to
      // specific variants only shows when the current size group is listed.
      if (!variantIds.length) return true;

      const sizeGroupIds = this.#variantIdsSharingSize(variantId);
      return variantIds.some((id) => sizeGroupIds.includes(Number(id)));
    }

    #hasVariantAssociations() {
      if (!this.#product?.media) return false;

      return this.#product.media.some(
        (item) =>
          Array.isArray(item.variant_ids) && item.variant_ids.length > 0,
      );
    }

    #filterMedia(variantId) {
      const wrapper = this.querySelector(".swiper-wrapper");

      if (!wrapper || !this.#hiddenHolder) return;

      const useFilter =
        this.#hasVariantAssociations() &&
        Boolean(variantId) &&
        this.#variantOwnsRun(variantId);

      for (const slide of this.#slides) {
        const mediaId = slide.dataset.mediaId;
        const visible =
          !useFilter || this.#mediaMatchesVariant(mediaId, variantId);

        slide.classList.toggle("is-hidden", !visible);

        if (visible) {
          wrapper.append(slide);
        } else {
          this.#hiddenHolder.append(slide);
        }
      }

      // Never leave the gallery blank — fall back to all media.
      if (useFilter && this.#visibleSlides().length === 0) {
        for (const slide of this.#slides) {
          slide.classList.remove("is-hidden");
          wrapper.append(slide);
        }
      }

      for (const thumbnail of this.#thumbnails) {
        const mediaId = thumbnail.dataset.mediaId;
        const slideVisible = this.#slides.some(
          (slide) =>
            slide.dataset.mediaId === mediaId &&
            !slide.classList.contains("is-hidden"),
        );

        thumbnail.classList.toggle("is-hidden", !slideVisible);
        thumbnail.hidden = !slideVisible;
      }
    }

    #visibleSlides() {
      return this.#slides.filter(
        (slide) => !slide.classList.contains("is-hidden"),
      );
    }

    #destroySwiper() {
      this.#swiper?.destroy(true, true);
      this.#swiper = null;
    }

    #reinitSwiper(initialSlide = 0) {
      this.#destroySwiper();
      this.#initSwiper(0, initialSlide);
    }

    #initSwiper(retryCount = 0, initialSlide = 0) {
      if (this.#swiper) return;

      if (typeof Swiper === "undefined") {
        if (retryCount >= 40) return;

        if (document.readyState === "complete") {
          window.setTimeout(
            () => this.#initSwiper(retryCount + 1, initialSlide),
            50,
          );
        } else {
          window.addEventListener(
            "load",
            () => this.#initSwiper(retryCount + 1, initialSlide),
            { once: true },
          );
        }
        return;
      }

      const swiperElement = this.querySelector(".swiper");

      if (!swiperElement) return;

      const visibleCount = this.#visibleSlides().length;

      if (visibleCount === 0) return;

      const prevButton = this.querySelector("[data-gallery-prev]");
      const nextButton = this.querySelector("[data-gallery-next]");
      const paginationElement = this.querySelector(".swiper-pagination");
      const controls = this.querySelector("[data-gallery-controls]");
      const nav = this.querySelector("[data-gallery-nav]");
      const thumbnails = this.querySelector("[data-gallery-thumbnails]");
      const hideDesktopNav = visibleCount < 2;
      const startIndex = Math.min(
        Math.max(0, initialSlide),
        Math.max(0, visibleCount - 1),
      );

      if (controls) {
        controls.hidden = hideDesktopNav;
      }

      if (nav) {
        nav.hidden = hideDesktopNav;
      }

      if (thumbnails) {
        thumbnails.hidden = visibleCount < 2;
      }

      this.#swiper = new Swiper(swiperElement, {
        initialSlide: startIndex,
        slidesPerView: 1,
        spaceBetween: 0,
        watchSlidesProgress: true,
        observer: true,
        observeParents: true,
        breakpoints: {
          1024: {
            slidesPerView: Math.min(2, visibleCount),
            spaceBetween: 8,
          },
        },
        pagination: paginationElement
          ? {
              el: paginationElement,
              clickable: true,
              bulletClass: "carousel-pagination__bullet",
              bulletActiveClass: "carousel-pagination__bullet--active",
            }
          : false,
        navigation: {
          prevEl: prevButton,
          nextEl: nextButton,
        },
        on: {
          slideChange: () => this.#syncThumbnails(),
          init: () => this.#syncThumbnails(),
        },
      });
    }

    #onThumbnailClick(event) {
      const thumbnail = event.currentTarget;
      const mediaId = thumbnail.dataset.mediaId;
      const index = this.#visibleSlides().findIndex(
        (slide) => slide.dataset.mediaId === mediaId,
      );

      if (index < 0) return;

      this.#swiper?.slideTo(index);
      this.#setActiveThumbnail(mediaId);
    }

    #syncThumbnails() {
      if (!this.#swiper) return;

      const activeSlide = this.#visibleSlides()[this.#swiper.activeIndex];

      if (!activeSlide) return;

      this.#setActiveThumbnail(activeSlide.dataset.mediaId);
    }

    #setActiveThumbnail(mediaId) {
      for (const thumbnail of this.#thumbnails) {
        const isActive = thumbnail.dataset.mediaId === mediaId;

        thumbnail.classList.toggle("is-active", isActive);
        thumbnail.setAttribute("aria-current", isActive ? "true" : "false");

        if (isActive && !window.matchMedia(DESKTOP_MQ).matches) {
          this.#centreThumbnail(thumbnail);
        }
      }
    }

    /**
     * Centres the active thumbnail in its own strip. scrollIntoView cannot be
     * used here: it walks every scrollable ancestor up to the document, so
     * whenever the strip is off-screen it drags the whole page back to the
     * gallery — which hijacked in-page anchor jumps such as the buy box's
     * "#reviews" link once a deferred Swiper init fired after the jump.
     */
    #centreThumbnail(thumbnail) {
      const strip = this.#thumbnailStrip;

      if (!strip) return;

      const overflow = strip.scrollWidth - strip.clientWidth;

      if (overflow <= 0) return;

      const target = Math.min(
        Math.max(
          thumbnail.offsetLeft +
            thumbnail.offsetWidth / 2 -
            strip.clientWidth / 2,
          0,
        ),
        overflow,
      );

      if (Math.abs(target - strip.scrollLeft) < 1) return;

      strip.scrollTo({ left: target, behavior: "smooth" });
    }

    #armDeferredVideos() {
      const videos = [...this.querySelectorAll("video[data-gallery-video]")];

      if (videos.length === 0) return;

      for (const video of videos) {
        video.addEventListener("click", () => {
          attachGalleryVideo(video);
          video.play()?.catch(() => {});
        });
      }

      const observe = () => {
        if (!("IntersectionObserver" in window)) {
          for (const video of videos) attachGalleryVideo(video);
          return;
        }

        const observer = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              if (!(entry.target instanceof HTMLVideoElement)) continue;

              attachGalleryVideo(entry.target);
              observer.unobserve(entry.target);
            }
          },
          { rootMargin: "200px" },
        );

        for (const video of videos) observer.observe(video);
      };

      if (document.readyState === "complete") {
        observe();
      } else {
        window.addEventListener("load", observe, { once: true });
      }
    }
  }

  function attachGalleryVideo(video) {
    if (!(video instanceof HTMLVideoElement)) return;
    if (video.dataset.galleryVideoLoaded === "true") return;

    const data = video.nextElementSibling;

    if (
      !(data instanceof HTMLScriptElement) ||
      !data.matches("[data-gallery-video-sources]")
    ) {
      return;
    }

    let sources = [];

    try {
      sources = JSON.parse(data.textContent || "[]");
    } catch {
      return;
    }

    if (!Array.isArray(sources)) return;

    for (const source of sources) {
      if (!source?.url) continue;

      const element = document.createElement("source");

      element.src = source.url;

      if (source.mime_type) element.type = source.mime_type;

      video.append(element);
    }

    video.dataset.galleryVideoLoaded = "true";
    video.preload = "auto";
    video.load();
  }

  if (!customElements.get(GALLERY_NAME)) {
    customElements.define(GALLERY_NAME, ProductGallery);
  }
})();
